import { apiService } from '../../../components/api/apiService';
import { OAUTH_API_BASE, OAUTH_CONNECTION } from './oauthAppsConstants';
import type { ClientType } from './oauthAppsConstants';
import type { OAuthApp, OAuthAppFormState } from './oauthAppsTypes';

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string }; status?: number };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp. Start it with `python run.py` from BackendPYApp.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'OAuth request failed'
  );
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function normalizeApp(raw: Record<string, unknown>): OAuthApp {
  return {
    id: String(raw.id || ''),
    clientId: String(raw.clientId || raw.client_id || ''),
    applicationName: String(raw.applicationName || raw.application_name || ''),
    redirectUris: asArray<string>(raw.redirectUris || raw.redirect_uris).map(String),
    scopes: asArray<string>(raw.scopes || raw.allowed_scopes).map(String),
    clientType: (String(raw.clientType || raw.client_type || 'confidential') as ClientType),
    hasClientSecret: Boolean(raw.hasClientSecret ?? raw.has_client_secret),
    clientSecretLast4: raw.clientSecretLast4 != null || raw.client_secret_last4 != null
      ? String(raw.clientSecretLast4 || raw.client_secret_last4)
      : null,
    clientSecret: raw.clientSecret || raw.client_secret ? String(raw.clientSecret || raw.client_secret) : null,
    createdBy: String(raw.createdBy || raw.created_by || ''),
    createdAt: String(raw.createdAt || raw.created_at || ''),
    updatedAt: String(raw.updatedAt || raw.updated_at || ''),
  };
}

function formPayload(form: OAuthAppFormState, includeType: boolean) {
  const payload: Record<string, unknown> = {
    applicationName: form.applicationName.trim(),
    redirectUris: form.redirectUris.map((uri) => uri.trim()).filter(Boolean),
    scopes: form.scopes,
  };
  if (includeType) payload.clientType = form.clientType;
  return payload;
}

export const oauthAppsApi = {
  async list(): Promise<OAuthApp[]> {
    try {
      const data = (await apiService.get(`${OAUTH_API_BASE}/apps`, {}, OAUTH_CONNECTION)) as {
        items?: Record<string, unknown>[];
      };
      return asArray<Record<string, unknown>>(data?.items).map(normalizeApp);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async create(form: OAuthAppFormState): Promise<OAuthApp> {
    try {
      const data = (await apiService.post(
        `${OAUTH_API_BASE}/apps`,
        formPayload(form, true),
        OAUTH_CONNECTION
      )) as Record<string, unknown>;
      return normalizeApp(data);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async update(id: string, form: OAuthAppFormState): Promise<OAuthApp> {
    try {
      const data = (await apiService.put(
        `${OAUTH_API_BASE}/apps/${id}`,
        formPayload(form, false),
        OAUTH_CONNECTION
      )) as Record<string, unknown>;
      return normalizeApp(data);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async remove(id: string): Promise<void> {
    try {
      await apiService.delete(`${OAUTH_API_BASE}/apps/${id}`, OAUTH_CONNECTION);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async rotateSecret(id: string): Promise<{ clientId: string; clientSecret: string }> {
    try {
      const data = (await apiService.post(
        `${OAUTH_API_BASE}/apps/${id}/rotate-secret`,
        {},
        OAUTH_CONNECTION
      )) as Record<string, unknown>;
      return {
        clientId: String(data.clientId || data.client_id || ''),
        clientSecret: String(data.clientSecret || data.client_secret || ''),
      };
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },
};
